<template>
  <v-container
    grid-list-xl
    fluid
  >
    <v-layout
      row
      wrap
    >
      <v-flex xs12>
        <FilterButtons
          :filters="appliedFilters"
          @filter-removed="removeFilter"
        />

        <MassActionSelect
          :actions="massActions"
          @action-selected="dispatchAction"
        />

        <CollectionFilters
          :filters="filters"
          :active-filters="appliedFilters"
          @filters-submitted="submitFilters"
          @filters-cleared="clearFilters"
        />

        <ProductExportImport
          class="right"
          @update-products-list="fetchCollection()"
        />
      </v-flex>
      <v-flex xs12>
        <v-card>
          <v-card-title class="justify-end">
            <v-flex
              xs12
              md6
              class="py-0"
            >
              <v-text-field
                :loading="loading"
                v-model="search"
                :label="$t('Search by product name')"
                hide-details
                append-icon="search"
              />
            </v-flex>
          </v-card-title>
          <v-divider />
          <v-data-table
            v-model="selected"
            :loading="loading"
            :headers="headers"
            :items="collection"
            :total-items="meta.totalItems"
            :rows-per-page-text="$t('Rows per page')"
            :rows-per-page-items="meta.rowsPerPageItems"
            :pagination.sync="pagination"
            show-select
            select-all
            item-key="id"
          >
            <v-progress-linear
              slot="progress"
              color="primary"
              indeterminate
            />

            <template v-slot:items="props">
              <ProductDataTableRow
                :props="props"
                :tax-type="taxType"
              />
            </template>

            <template slot="no-data">
              <v-btn
                color="primary"
                @click="clearFilters"
              >
                {{ $t('Reset') }}
              </v-btn>
            </template>
            <template v-slot:pageText="props">
              <span>{{ $t('{0} - {1} of {2}', [props.pageStart, props.pageStop, props.itemsLength]) }}</span>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import debounce from 'lodash.debounce'
import ProductDataTableRow from '../components/ProductDataTableRow'
import MassActionSelect from '../../../vuetify-console/components/MassActionSelect'
import CollectionFilters from '../../../vuetify-console/components/CollectionFilters'
import FilterButtons from '../../../vuetify-console/components/FilterButtons'
import ProductExportImport from '../components/ProductExportImport'

import { createNamespacedHelpers } from 'vuex'
const { mapActions, mapGetters } = createNamespacedHelpers('catalog/products')

export default {
  name: 'ProductList',

  components: {
    ProductDataTableRow,
    MassActionSelect,
    CollectionFilters,
    FilterButtons,
    ProductExportImport
  },

  actionBar () {
    return {
      title: 'Products',
      actions: [
        {
          text: 'Create Product',
          event: 'add'
        }
      ]
    }
  },

  data () {
    return {
      selected: []
    }
  },

  computed: {
    ...mapGetters(['collection', 'loading', 'meta', 'appliedFilters', 'taxType']),

    pagination: {
      get: function () {
        return this.meta
      },
      set: function (value) {
        this.applyMeta(value)
      }
    },

    headers () {
      const headers = [
        { text: this.$t('Sort Order'), value: 'order' },
        { text: this.$t('Image'), value: null, sortable: false },
        { text: this.$t('SKU'), value: 'sku' },
        { text: this.$t('Name'), value: 'name' },
        { text: this.$t('Class'), value: 'classification' },
        { text: this.$t('Price'), value: 'price.amount' },
        { text: this.$t('Sale Price'), value: 'sale_price.amount', sortable: false }
      ]

      if (this.taxType === 'vat') {
        headers.push(
          { text: this.$t('Gross Price'), value: 'gross_price.amount', sortable: false },
          { text: this.$t('Gross Sale Price'), value: 'gross_sale_price.amount', sortable: false }
        )
      }

      headers.push(
        { text: this.$t('Quantity'), value: 'quantity' },
        { text: this.$t('Created At'), value: 'created_at' }
      )

      return headers
    },

    massActions () {
      return [
        { code: 'delete', label: this.$t('Delete') },
        { code: 'reindex', label: this.$t('Reindex') }
      ]
    },

    filters () {
      return [
        { field: 'sku', type: 'string', label: this.$t('SKU') },
        { field: 'name', type: 'string', label: this.$t('Name') },
        { field: 'price.amount', parentField: 'price', type: 'money', label: this.$t('Price') }
      ]
    },

    nameFilter () {
      return (this.appliedFilters || []).find(filter => filter.field === 'name')
    },

    search: {
      set: debounce(function (value) {
        let filters = [...this.appliedFilters || []]

        if (!value) {
          filters = filters.filter(filter => filter.field !== 'name')
          this.submitFilters(filters)
          return
        }

        const filter = filters.find(f => f.field === 'name')
        if (filter) {
          filter.value = value
          this.submitFilters(filters)
          return
        }

        filters.push({
          label: this.$t('Name'),
          field: 'name',
          type: 'string',
          operator: 'like',
          value
        })
        this.submitFilters(filters)
      }, 500),
      get () {
        return this.nameFilter ? this.nameFilter.value : ''
      }
    }
  },

  mounted () {
    this.fetchTaxSettings()
  },

  methods: {
    ...mapActions(['applyMeta', 'fetchCollection', 'removeFilter', 'clearFilters', 'fetchTaxSettings']),

    onActionAdd () {
      this.$router.push({ name: 'single-product', params: { id: '__' } })
    },

    dispatchAction (action) {
      if (action === 'delete') {
        this.deleteSelected()
      }

      if (action === 'reindex') {
        this.reindexSelected()
      }
    },

    async deleteSelected () {
      if (!this.selected.length) {
        return
      }

      let products = null
      if (this.selected.length) {
        products = this.selected.map(item => item.id)
      }

      await this.$http.make('product').massDelete(products)
      this.selected = []
      this.$toast.success(this.$t('Deleting records.'))
    },

    async reindexSelected () {
      let products = null
      if (this.selected.length) {
        products = this.selected.map(item => item.id)
      }

      await this.$http.make('product').reindex(products)

      this.$toast.success(this.$t('Products reindex started.'))
    },

    submitFilters (filters) {
      this.applyMeta({ filters })
    }
  }
}
</script>
